import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  startOfWeek, endOfWeek, isSameDay, isToday, addMonths, subMonths,
  startOfDay,
} from 'date-fns'
import {
  Briefcase, Plus, Search, ChevronLeft, ChevronRight,
  List, LayoutGrid, Calendar, Users, DollarSign, ChevronRight as Arrow,
} from 'lucide-react'

// ─── Constants ───────────────────────────────────────────────────────────────

const JOB_STATUSES = ['confirmed', 'staffing', 'crew_set', 'in_progress', 'completed', 'cancelled']

const STATUS_LABELS = {
  confirmed: 'Confirmed', staffing: 'Staffing', crew_set: 'Crew Set',
  in_progress: 'In Progress', completed: 'Completed', cancelled: 'Cancelled',
}

const STATUS_COLORS = {
  confirmed: '#3b82f6', staffing: '#8b5cf6', crew_set: '#06b6d4',
  in_progress: '#f59e0b', completed: '#10b981', cancelled: '#9ca3af',
}

const SUCCESS_FEEDBACK_MS = 5000

// ─── Calendar helpers ─────────────────────────────────────────────────────────

function buildCalendarGrid(month) {
  const start = startOfWeek(startOfMonth(month), { weekStartsOn: 0 })
  const end = endOfWeek(endOfMonth(month), { weekStartsOn: 0 })
  return eachDayOfInterval({ start, end })
}

// ─── Job Card (used in pipeline) ─────────────────────────────────────────────

function JobCard({ job, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: 'white', borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)', padding: '12px 14px',
        cursor: 'pointer', marginBottom: 8,
        boxShadow: 'var(--shadow-sm)',
        transition: 'box-shadow 0.12s, transform 0.12s',
      }}
      onMouseOver={e => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.transform = 'translateY(-1px)' }}
      onMouseOut={e => { e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; e.currentTarget.style.transform = 'none' }}
    >
      <div style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--color-text-muted)', marginBottom: 4, textTransform: 'capitalize' }}>
        {job.event_type || 'Event'} · {job.event_date ? format(new Date(job.event_date + 'T12:00:00'), 'MMM d') : '—'}
      </div>
      <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)', marginBottom: 4 }}>
        {job.clients?.name || '—'}
      </div>
      {job.address && (
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {job.address}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {job.crew_required > 0 && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            <Users size={11} /> {job.crew_required}
          </span>
        )}
        <span className={`badge badge-${job.payment_status}`} style={{ fontSize: 10, padding: '1px 6px' }}>
          {job.payment_status === 'paid_in_full' ? 'Paid' : job.payment_status === 'deposit_paid' ? 'Deposit' : job.payment_status}
        </span>
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function JobsPage() {
  const navigate = useNavigate()
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [rowActionLoadingId, setRowActionLoadingId] = useState(null)
  const [view, setView] = useState('schedule') // 'schedule' | 'list' | 'pipeline' | 'calendar'
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [scheduleFilter, setScheduleFilter] = useState('all')
  const [calMonth, setCalMonth] = useState(new Date())
  const scheduleGroupRefs = useRef({})
  const hasAutoFocusedRef = useRef(false)

  const loadJobs = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('*, clients(name, email), job_staff(id)')
        .order('event_date', { ascending: true })

      if (error) throw error
      setJobs(data || [])
    } catch (err) {
      console.error('Failed to load jobs:', err)
      setError(err?.message || 'Failed to load jobs.')
      setJobs([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadJobs() }, [loadJobs])

  async function updateJobStatusQuick(jobId, nextStatus) {
    try {
      setRowActionLoadingId(jobId)
      setError('')
      setSuccess('')

      const { error } = await supabase
        .from('jobs')
        .update({ status: nextStatus })
        .eq('id', jobId)

      if (error) throw error

      await loadJobs()
      setSuccess(`Job marked ${STATUS_LABELS[nextStatus] || nextStatus}.`)
      setTimeout(() => setSuccess(''), SUCCESS_FEEDBACK_MS)
    } catch (err) {
      console.error('Quick status update failed:', err)
      setError(err?.message || 'Failed to update job status.')
    } finally {
      setRowActionLoadingId(null)
    }
  }

  // Filtered jobs for list/pipeline
  const filtered = jobs.filter(j => {
    const matchStatus = statusFilter === 'all' || j.status === statusFilter
    const term = search.toLowerCase()
    const matchSearch = !term ||
      j.clients?.name?.toLowerCase().includes(term) ||
      j.address?.toLowerCase().includes(term) ||
      j.event_type?.toLowerCase().includes(term)
    return matchStatus && matchSearch
  })

  // Stats
  const active = jobs.filter(j => !['completed', 'cancelled'].includes(j.status))
  const completedCount = jobs.filter(j => j.status === 'completed').length
  const outstanding = jobs
    .filter(j => j.status !== 'cancelled')
    .reduce((s, j) => s + parseFloat(j.balance_due || 0), 0)

  // Pipeline: group by status (exclude cancelled unless filtered to it)
  const pipelineStatuses = statusFilter === 'cancelled'
    ? ['cancelled']
    : JOB_STATUSES.filter(s => s !== 'cancelled')
  const byStatus = {}
  pipelineStatuses.forEach(s => {
    byStatus[s] = filtered.filter(j => j.status === s)
  })

  // Calendar: jobs by date
  const calGrid = buildCalendarGrid(calMonth)
  function jobsOnDay(day) {
    return jobs.filter(j =>
      j.event_date && isSameDay(new Date(j.event_date + 'T12:00:00'), day) &&
      j.status !== 'cancelled'
    )
  }

  const isActiveJob = j => !['completed', 'cancelled'].includes(j.status)
  const filteredForSchedule = jobs.filter(j => {
    if (scheduleFilter === 'active') return isActiveJob(j)
    if (scheduleFilter === 'completed') return j.status === 'completed'
    return true
  })

  const todayStart = startOfDay(new Date())
  const unscheduledJobs = filteredForSchedule.filter(j => !j.event_date)
  const datedJobs = filteredForSchedule
    .filter(j => j.event_date)
    .map(j => ({ ...j, _eventDate: new Date(j.event_date + 'T12:00:00') }))

  const todayJobs = datedJobs
    .filter(j => isSameDay(j._eventDate, todayStart))
    .sort((a, b) => a._eventDate - b._eventDate)

  const upcomingJobs = datedJobs
    .filter(j => j._eventDate > todayStart && !isSameDay(j._eventDate, todayStart))
    .sort((a, b) => a._eventDate - b._eventDate)

  const recentPastJobs = datedJobs
    .filter(j => j._eventDate < todayStart && !isSameDay(j._eventDate, todayStart))
    .sort((a, b) => b._eventDate - a._eventDate)
    .slice(0, 14)

  const scheduleGroups = [
    { key: 'today', title: 'Today', items: todayJobs },
    { key: 'upcoming', title: 'Upcoming', items: upcomingJobs },
    { key: 'recent_past', title: 'Recent Past', items: recentPastJobs },
    { key: 'unscheduled', title: 'Unscheduled', items: unscheduledJobs },
  ]

  const scheduleNeedsCrewCount = filteredForSchedule.filter(j => {
    const assigned = Array.isArray(j.job_staff) ? j.job_staff.length : 0
    return assigned === 0 && j.status !== 'completed'
  }).length
  const scheduleUpcomingSoonCount = filteredForSchedule.filter(j => {
    if (!j.event_date) return false
    const d = new Date(j.event_date + 'T12:00:00')
    const diff = Math.floor((startOfDay(d).getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24))
    return diff > 0 && diff <= 3
  }).length
  const scheduleCompletedCount = filteredForSchedule.filter(j => j.status === 'completed').length
  const scheduleTriage = {
    today: todayJobs.length,
    needsCrew: scheduleNeedsCrewCount,
    unscheduled: unscheduledJobs.length,
    upcomingSoon: scheduleUpcomingSoonCount,
    completed: scheduleCompletedCount,
  }

  const primaryFocusGroupKey = useMemo(() => {
    const order = ['today', 'upcoming', 'unscheduled', 'recent_past']
    const firstNonEmpty = order.find(key => {
      const g = scheduleGroups.find(sg => sg.key === key)
      return g && g.items.length > 0
    })
    return firstNonEmpty || 'today'
  }, [scheduleGroups])

  useEffect(() => {
    if (view !== 'schedule') {
      hasAutoFocusedRef.current = false
      return
    }
    if (loading || hasAutoFocusedRef.current) return

    const el = scheduleGroupRefs.current[primaryFocusGroupKey]
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      hasAutoFocusedRef.current = true
    }
  }, [view, loading, primaryFocusGroupKey])

  if (loading) return <div style={{ padding: 32, color: 'var(--color-text-muted)' }}>Loading jobs…</div>

  return (
    <div data-testid={view === 'schedule' ? 'schedule-page' : undefined}>
      {error && <div className="alert alert-error" data-testid="feedback-banner-error" style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className="alert alert-success" data-testid="feedback-banner-success" style={{ marginBottom: 16 }}>{success}</div>}

      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Schedule</h1>
          <p className="page-subtitle">Plan upcoming work, crew readiness, and job progress</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/jobs/new')}>
          <Plus size={15} /> Create Job
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid" data-testid={view === 'schedule' ? 'schedule-summary-section' : undefined} style={{ marginBottom: 'var(--sp-5)' }}>
        <div className="stat-card">
          <div className="stat-label">Total Jobs</div>
          <div className="stat-value">{jobs.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active</div>
          <div className="stat-value" style={{ color: 'var(--color-navy)' }}>{active.length}</div>
          <div className="stat-sub">confirmed → in progress</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Completed</div>
          <div className="stat-value" style={{ color: 'var(--color-green)' }}>{completedCount}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Outstanding Balance</div>
          <div className="stat-value" style={{ fontSize: 'var(--text-xl)', color: outstanding > 0 ? 'var(--color-amber)' : undefined }}>
            ${outstanding.toLocaleString('en-CA', { minimumFractionDigits: 0 })}
          </div>
          <div className="stat-sub">across all active jobs</div>
        </div>
      </div>

      {/* View Switcher + Filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--sp-4)', flexWrap: 'wrap' }}>
        {/* View tabs */}
        <div style={{ display: 'flex', gap: 2, background: 'var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 2 }}>
          {[
            { key: 'schedule', icon: Calendar, label: 'Schedule' },
            { key: 'list', icon: List, label: 'List' },
            { key: 'pipeline', icon: LayoutGrid, label: 'Pipeline' },
            { key: 'calendar', icon: Calendar, label: 'Calendar' },
          ].map(({ key, icon: Icon, label }) => (
            <button key={key} onClick={() => setView(key)} style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 12px', border: 'none', cursor: 'pointer',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'var(--font-body)', fontSize: 'var(--text-xs)', fontWeight: 500,
              background: view === key ? 'white' : 'transparent',
              color: view === key ? 'var(--color-text)' : 'var(--color-text-muted)',
              boxShadow: view === key ? 'var(--shadow-sm)' : 'none',
              transition: 'all 0.12s',
            }}>
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        {/* Schedule filter */}
        {view === 'schedule' && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button data-testid="schedule-filter-all" className={`btn btn-sm ${scheduleFilter === 'all' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setScheduleFilter('all')}>All</button>
            <button data-testid="schedule-filter-active" className={`btn btn-sm ${scheduleFilter === 'active' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setScheduleFilter('active')}>Active/Open</button>
            <button data-testid="schedule-filter-completed" className={`btn btn-sm ${scheduleFilter === 'completed' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setScheduleFilter('completed')}>Completed</button>
          </div>
        )}

        {/* Status filter (list + pipeline only) */}
        {view !== 'calendar' && view !== 'schedule' && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {['all', ...JOB_STATUSES].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)} style={{
                padding: '4px 10px', border: '1px solid',
                borderColor: statusFilter === s ? STATUS_COLORS[s] || 'var(--color-navy)' : 'var(--color-border)',
                borderRadius: 20, cursor: 'pointer', fontSize: 'var(--text-xs)', fontWeight: 500,
                fontFamily: 'var(--font-body)',
                background: statusFilter === s ? (STATUS_COLORS[s] || 'var(--color-navy)') : 'white',
                color: statusFilter === s ? 'white' : 'var(--color-text-secondary)',
                transition: 'all 0.12s',
              }}>
                {s === 'all' ? 'All' : STATUS_LABELS[s]}
                {s !== 'all' && (
                  <span style={{ marginLeft: 5, opacity: 0.75 }}>
                    {jobs.filter(j => j.status === s).length}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Search */}
        {view !== 'calendar' && view !== 'schedule' && (
          <div className="search-bar" style={{ marginLeft: 'auto' }}>
            <Search size={14} className="search-icon" />
            <input placeholder="Search client, address…" value={search}
              onChange={e => setSearch(e.target.value)} />
          </div>
        )}
      </div>

      {/* ── LIST VIEW ──────────────────────────────────────────────────── */}
      {view === 'schedule' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          <div data-testid="schedule-triage-strip" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 10 }}>
            <div data-testid="schedule-metric-today" className="stat-card" style={{ padding: 12 }}>
              <div className="stat-label">Jobs today</div>
              <div className="stat-value" style={{ fontSize: 'var(--text-xl)' }}>{scheduleTriage.today}</div>
            </div>
            <div data-testid="schedule-metric-needs-crew" className="stat-card" style={{ padding: 12, borderColor: 'var(--color-amber)', background: '#fffaf0' }}>
              <div className="stat-label">Need crew</div>
              <div className="stat-value" style={{ fontSize: 'var(--text-xl)', color: 'var(--color-amber)' }}>{scheduleTriage.needsCrew}</div>
            </div>
            <div data-testid="schedule-metric-unscheduled" className="stat-card" style={{ padding: 12, borderColor: '#f59e0b33' }}>
              <div className="stat-label">Unscheduled</div>
              <div className="stat-value" style={{ fontSize: 'var(--text-xl)' }}>{scheduleTriage.unscheduled}</div>
            </div>
            <div data-testid="schedule-metric-upcoming-soon" className="stat-card" style={{ padding: 12 }}>
              <div className="stat-label">Upcoming soon</div>
              <div className="stat-value" style={{ fontSize: 'var(--text-xl)' }}>{scheduleTriage.upcomingSoon}</div>
            </div>
            <div data-testid="schedule-metric-completed" className="stat-card" style={{ padding: 12 }}>
              <div className="stat-label">Completed</div>
              <div className="stat-value" style={{ fontSize: 'var(--text-xl)' }}>{scheduleTriage.completed}</div>
            </div>
          </div>
          {scheduleGroups.map(group => (
            <div
              key={group.key}
              ref={el => { scheduleGroupRefs.current[group.key] = el }}
              className="card"
              data-testid={group.key === 'recent_past' ? 'schedule-group-recent-past' : `schedule-group-${group.key}`}
              style={group.key === primaryFocusGroupKey ? { borderColor: 'var(--color-navy)', boxShadow: '0 0 0 2px rgba(20,46,78,0.08)' } : undefined}
            >
              <div
                className="card-header"
                style={{ padding: '14px 18px', background: group.key === primaryFocusGroupKey ? 'rgba(20,46,78,0.04)' : undefined }}
                data-testid={group.key === primaryFocusGroupKey ? 'schedule-primary-focus-group' : undefined}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700 }}>{group.title}</span>
                  <span className="badge badge-draft">{group.items.length}</span>
                  {group.key === primaryFocusGroupKey && <span className="badge badge-confirmed">Primary focus</span>}
                </div>
              </div>
              {group.items.length === 0 ? (
                <div data-testid={`empty-state-schedule-group-${group.key === 'recent_past' ? 'recent-past' : group.key}`} style={{ padding: '14px 18px', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
                  {group.key === 'today' && 'No jobs scheduled for today. You can focus on upcoming work.'}
                  {group.key === 'upcoming' && 'No upcoming jobs in this filter.'}
                  {group.key === 'unscheduled' && 'No unscheduled jobs right now.'}
                  {group.key === 'recent_past' && 'No recent past jobs to review.'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {group.items.map(job => {
                    const hasDate = !!job.event_date
                    const jobDay = hasDate ? new Date(job.event_date + 'T12:00:00') : null
                    const dayDiff = jobDay ? Math.floor((startOfDay(jobDay).getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24)) : null
                    const isJobToday = !!jobDay && isSameDay(jobDay, todayStart)
                    const isUpcomingSoon = dayDiff !== null && dayDiff > 0 && dayDiff <= 3
                    const isCompleted = job.status === 'completed'
                    const isUnscheduled = !hasDate
                    const assignedCount = Array.isArray(job.job_staff) ? job.job_staff.length : 0
                    const hasCrewAssigned = assignedCount > 0

                    return (
                      <div
                        key={job.id}
                        data-testid={`schedule-job-row-${job.id}`}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '180px 1.2fr 1fr 170px 200px',
                          gap: 12,
                          alignItems: 'center',
                          padding: '10px 18px',
                          borderTop: '1px solid var(--color-border)',
                        }}
                      >
                        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                          {job.event_date ? format(new Date(job.event_date + 'T12:00:00'), 'EEE, MMM d, yyyy') : 'Unscheduled'}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600 }}>{job.clients?.name || 'Unknown client'}</div>
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                            {job.event_type || 'Job'}
                          </div>
                        </div>
                        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {job.address || '—'}
                        </div>
                        <div>
                          <span className={`badge badge-${job.status}`}>{STATUS_LABELS[job.status] || job.status}</span>
                          <div data-testid={`schedule-job-attention-${job.id}`} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                            {isUnscheduled && <span data-testid={`schedule-job-badge-unscheduled-${job.id}`} className="badge badge-cancelled">Unscheduled</span>}
                            {isJobToday && <span data-testid={`schedule-job-badge-today-${job.id}`} className="badge badge-confirmed">Today</span>}
                            {isUpcomingSoon && <span data-testid={`schedule-job-badge-upcoming-soon-${job.id}`} className="badge badge-staffing">Upcoming soon</span>}
                            {isCompleted && <span data-testid={`schedule-job-badge-completed-${job.id}`} className="badge badge-completed">Completed</span>}
                            <span data-testid={`schedule-job-crew-status-${job.id}`} className={`badge ${hasCrewAssigned ? 'badge-crew_set' : 'badge-staffing'}`}>
                              {hasCrewAssigned ? `Crew assigned (${assignedCount})` : 'Needs crew'}
                            </span>
                            {!hasCrewAssigned && !isCompleted && (
                              <span data-testid={`schedule-job-badge-needs-crew-${job.id}`} className="badge badge-staffing">Needs crew</span>
                            )}
                            {hasCrewAssigned && (
                              <span data-testid={`schedule-job-badge-crew-assigned-${job.id}`} className="badge badge-crew_set">Crew assigned</span>
                            )}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: 6, flexWrap: 'wrap' }}>
                          {rowActionLoadingId === job.id && (
                            <span data-testid={`schedule-job-action-status-loading-${job.id}`} style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', alignSelf: 'center' }}>
                              Updating…
                            </span>
                          )}
                          <div data-testid={`schedule-job-link-${job.id}`}>
                            <button
                              data-testid={`schedule-job-action-open-${job.id}`}
                              className="btn btn-ghost btn-sm"
                              onClick={() => navigate(`/jobs/${job.id}`)}
                            >
                              Open
                            </button>
                          </div>

                          {(job.status === 'confirmed' || job.status === 'staffing' || job.status === 'crew_set') && (
                            <button
                              data-testid={`schedule-job-action-start-${job.id}`}
                              className="btn btn-secondary btn-sm"
                              disabled={rowActionLoadingId === job.id}
                              onClick={() => updateJobStatusQuick(job.id, 'in_progress')}
                            >
                              Start
                            </button>
                          )}
                          {job.status === 'in_progress' && (
                            <button
                              data-testid={`schedule-job-action-complete-${job.id}`}
                              className="btn btn-secondary btn-sm"
                              disabled={rowActionLoadingId === job.id}
                              onClick={() => updateJobStatusQuick(job.id, 'completed')}
                            >
                              Complete
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {view === 'list' && (
        <div className="card">
          {filtered.length === 0 ? (
            <div className="empty-state" data-testid="empty-state-jobs-list">
              <Briefcase size={36} className="empty-state-icon" />
              <p style={{ fontWeight: 600 }}>No jobs found</p>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                {search || statusFilter !== 'all'
                  ? 'No jobs match your current filters. Clear filters to see more jobs.'
                  : 'No jobs yet. Create a job or convert an accepted quote to get started.'}
              </p>
              {search || statusFilter !== 'all' ? (
                <button className="btn btn-secondary" onClick={() => { setSearch(''); setStatusFilter('all') }}>
                  Clear Filters
                </button>
              ) : (
                <button className="btn btn-primary" onClick={() => navigate('/jobs/new')}>
                  <Plus size={14} /> Create Job
                </button>
              )}
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Event Date</th>
                    <th>Client</th>
                    <th>Event Type</th>
                    <th>Address</th>
                    <th>Status</th>
                    <th>Crew</th>
                    <th>Payment</th>
                    <th>Balance Due</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(job => (
                    <tr key={job.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/jobs/${job.id}`)}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>
                          {job.event_date ? format(new Date(job.event_date + 'T12:00:00'), 'MMM d, yyyy') : '—'}
                        </div>
                        {job.setup_date && job.setup_date !== job.event_date && (
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                            Setup: {format(new Date(job.setup_date + 'T12:00:00'), 'MMM d')}
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{job.clients?.name || '—'}</div>
                      </td>
                      <td style={{ textTransform: 'capitalize', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                        {job.event_type || '—'}
                      </td>
                      <td style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {job.address || '—'}
                      </td>
                      <td>
                        <span className={`badge badge-${job.status}`}>{STATUS_LABELS[job.status]}</span>
                      </td>
                      <td style={{ fontSize: 'var(--text-sm)' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Users size={12} style={{ color: 'var(--color-text-muted)' }} />
                          {job.crew_required || 0}
                        </span>
                      </td>
                      <td>
                        <span className={`badge badge-${job.payment_status}`}>
                          {job.payment_status === 'paid_in_full' ? 'Paid' :
                            job.payment_status === 'deposit_paid' ? 'Deposit Paid' :
                            job.payment_status === 'overdue' ? 'Overdue' : 'Pending'}
                        </span>
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', fontWeight: 600, color: parseFloat(job.balance_due) > 0 ? 'var(--color-amber)' : 'var(--color-green)' }}>
                        ${parseFloat(job.balance_due || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td>
                        <Arrow size={14} style={{ color: 'var(--color-text-muted)' }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── PIPELINE VIEW ──────────────────────────────────────────────── */}
      {view === 'pipeline' && (
        <div style={{ overflowX: 'auto', paddingBottom: 16 }}>
          <div style={{ display: 'flex', gap: 16, minWidth: 'max-content', alignItems: 'flex-start' }}>
            {pipelineStatuses.map(status => (
              <div key={status} style={{ width: 260, flexShrink: 0 }}>
                {/* Column header */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  marginBottom: 10, padding: '6px 2px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: STATUS_COLORS[status] }} />
                    <span style={{ fontWeight: 700, fontSize: 'var(--text-sm)' }}>{STATUS_LABELS[status]}</span>
                  </div>
                  <span style={{
                    background: 'var(--color-border)', borderRadius: 20,
                    padding: '2px 8px', fontSize: 'var(--text-xs)', fontWeight: 600,
                    color: 'var(--color-text-muted)',
                  }}>
                    {byStatus[status]?.length || 0}
                  </span>
                </div>
                {/* Column background */}
                <div style={{ background: 'var(--color-bg)', borderRadius: 'var(--radius-md)', padding: '10px 8px', minHeight: 120 }}>
                  {(byStatus[status] || []).length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px 0', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                      No jobs
                    </div>
                  ) : (byStatus[status] || []).map(job => (
                    <JobCard key={job.id} job={job} onClick={() => navigate(`/jobs/${job.id}`)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── CALENDAR VIEW ──────────────────────────────────────────────── */}
      {view === 'calendar' && (
        <div className="card">
          {/* Calendar header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--color-border)' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setCalMonth(m => subMonths(m, 1))}>
              <ChevronLeft size={16} />
            </button>
            <span style={{ fontWeight: 700, fontSize: 'var(--text-md)' }}>
              {format(calMonth, 'MMMM yyyy')}
            </span>
            <button className="btn btn-ghost btn-sm" onClick={() => setCalMonth(m => addMonths(m, 1))}>
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Day-of-week headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid var(--color-border)' }}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} style={{ padding: '8px 0', textAlign: 'center', fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {d}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {calGrid.map((day, i) => {
              const isCurrentMonth = day.getMonth() === calMonth.getMonth()
              const todayDay = isToday(day)
              const dayJobs = jobsOnDay(day)
              const isLast = i === calGrid.length - 1
              const isLastRow = i >= calGrid.length - 7

              return (
                <div key={day.toISOString()} style={{
                  minHeight: 90, padding: '8px 6px',
                  borderRight: (i + 1) % 7 === 0 ? 'none' : '1px solid var(--color-border)',
                  borderBottom: isLastRow ? 'none' : '1px solid var(--color-border)',
                  background: !isCurrentMonth ? '#fafbfc' : 'white',
                  opacity: isCurrentMonth ? 1 : 0.5,
                }}>
                  <div style={{
                    width: 26, height: 26, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: 4,
                    background: todayDay ? 'var(--color-navy)' : 'transparent',
                    color: todayDay ? 'white' : isCurrentMonth ? 'var(--color-text)' : 'var(--color-text-muted)',
                    fontSize: 'var(--text-xs)', fontWeight: todayDay ? 700 : 400,
                  }}>
                    {format(day, 'd')}
                  </div>
                  {dayJobs.slice(0, 3).map(job => (
                    <div
                      key={job.id}
                      onClick={() => navigate(`/jobs/${job.id}`)}
                      style={{
                        background: STATUS_COLORS[job.status] + '22',
                        borderLeft: `3px solid ${STATUS_COLORS[job.status]}`,
                        borderRadius: 3, padding: '2px 5px',
                        fontSize: 10, fontWeight: 600,
                        color: STATUS_COLORS[job.status],
                        marginBottom: 2, cursor: 'pointer',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                      title={`${job.clients?.name} — ${job.event_type || 'Event'}`}
                    >
                      {job.clients?.name || 'Job'}
                    </div>
                  ))}
                  {dayJobs.length > 3 && (
                    <div style={{ fontSize: 10, color: 'var(--color-text-muted)', paddingLeft: 2 }}>
                      +{dayJobs.length - 3} more
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Legend */}
          <div style={{ padding: '10px 20px', borderTop: '1px solid var(--color-border)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {JOB_STATUSES.filter(s => s !== 'cancelled').map(s => (
              <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: STATUS_COLORS[s], display: 'inline-block' }} />
                {STATUS_LABELS[s]}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
